import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Play, Star, Calendar, Users, DollarSign, BarChart3 } from 'lucide-react';

const Hero = () => {
  const stats = [
    { icon: <Calendar size={24} />, value: "30 Days", label: "Free Trial" },
    { icon: <Users size={24} />, value: "Customer", label: "Management" },
    { icon: <DollarSign size={24} />, value: "Expense", label: "Tracking" },
    { icon: <BarChart3 size={24} />, value: "Real-Time", label: "Dashboard" }
  ];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-primary">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-primary"></div>
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
            `,
            backgroundSize: '400px 400px',
            animation: 'float 20s ease-in-out infinite'
          }}
        ></div>
      </div>
      
      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center min-h-screen pt-8 pb-20">
          <motion.div
            className="text-white"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <motion.div
              className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md border border-white/30 rounded-full px-4 py-2 text-sm font-medium mb-8"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Star className="w-4 h-4 text-yellow-400" />
              <span>Trusted by 100+ spa professionals</span>
            </motion.div>

            <motion.h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              Transform Your Spa Business with{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent font-extrabold">Professional Management</span>
            </motion.h1>

            <motion.p
              className="text-xl leading-relaxed text-white/90 mb-10 max-w-lg"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Streamline operations on mobile and web. Boost revenue and deliver exceptional customer experiences with our comprehensive spa management solution. Start your free trial today.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <Link to="/demo" className="inline-flex items-center gap-2 btn btn-large bg-gradient-to-r from-pink-500 to-orange-500 text-white border-2 border-transparent hover:from-pink-600 hover:to-orange-600 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-pink-500/30 transition-all duration-300">
                <Play className="mr-2" size={20} />
                Watch Demo
              </Link>
            </motion.div>

            <motion.div
              className="grid grid-cols-2 gap-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="flex items-center gap-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                >
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-white">
                    {stat.icon}
                  </div>
                  <div className="flex-1">
                    <div className="text-2xl font-bold text-white leading-none">{stat.value}</div>
                    <div className="text-sm text-white/80 mt-1">{stat.label}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            className="flex justify-center items-center"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="w-80 h-[600px] bg-gray-900 rounded-[3rem] shadow-2xl overflow-hidden transform perspective-1000 -rotate-y-2 rotate-x-1 transition-transform duration-300 hover:rotate-y-0 hover:rotate-x-0 relative">
              {/* Mobile Frame */}
              <div className="absolute inset-0 bg-gray-900 rounded-[3rem] p-2">
                <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden">
                  {/* Status Bar */}
                  <div className="bg-gradient-to-r from-primary-500 to-secondary-500 px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="text-white text-xs font-medium">9:41</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-1 h-1 bg-white rounded-full"></div>
                      <div className="w-1 h-1 bg-white rounded-full"></div>
                      <div className="w-1 h-1 bg-white rounded-full"></div>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-2 bg-white rounded-sm"></div>
                      <div className="w-6 h-2 bg-white rounded-sm"></div>
                    </div>
                  </div>
                  
                  {/* App Header */}
                  <div className="bg-gradient-to-r from-primary-500 to-secondary-500 px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
                      <img 
                        src="/app_icon_512.png" 
                        alt="Wellness Pro" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="text-white font-semibold text-sm">Wellness Pro</div>
                  </div>
                  
                  {/* Mobile Content */}
                  <div className="p-4 bg-white h-full overflow-y-auto">
                    {/* Header with date and spa name */}
                    <div className="mb-4">
                      <div className="text-xs text-gray-500">Today, Dec 15, 2024</div>
                      <div className="text-sm font-bold text-gray-900">Serenity Spa Center</div>
                    </div>
                    
                    {/* Financial Overview Section */}
                    <div className="mb-4">
                      <div className="text-xs font-semibold text-gray-700 mb-2">Financial Overview</div>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                          <div className="text-sm font-bold text-green-800">₹45,230</div>
                          <div className="text-xs text-green-600">Total Revenue</div>
                          <div className="text-xs text-green-500">↗ +12.5%</div>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                          <div className="text-sm font-bold text-red-800">₹8,500</div>
                          <div className="text-xs text-red-600">Total Expenses</div>
                          <div className="text-xs text-red-500">↗ +5.2%</div>
                        </div>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                        <div className="text-sm font-bold text-blue-800">₹36,730</div>
                        <div className="text-xs text-blue-600">Net Balance</div>
                        <div className="text-xs text-blue-500">↗ +15.8%</div>
                      </div>
                    </div>
                    
                    {/* Payment Methods Breakdown */}
                    <div className="mb-4">
                      <div className="text-xs font-semibold text-gray-700 mb-2">Payment Methods</div>
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-xs text-gray-600">Cash</span>
                          </div>
                          <span className="text-xs font-semibold text-gray-800">₹28,500</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span className="text-xs text-gray-600">Card</span>
                          </div>
                          <span className="text-xs font-semibold text-gray-800">₹12,300</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <span className="text-xs text-gray-600">UPI</span>
                          </div>
                          <span className="text-xs font-semibold text-gray-800">₹4,430</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Room Status */}
                    <div className="mb-4">
                      <div className="text-xs font-semibold text-gray-700 mb-2">Room Status</div>
                      <div className="grid grid-cols-3 gap-1">
                        <div className="bg-green-100 border border-green-300 rounded p-1 text-center">
                          <div className="text-xs font-semibold text-green-800">R1</div>
                          <div className="text-xs text-green-600">Free</div>
                        </div>
                        <div className="bg-red-100 border border-red-300 rounded p-1 text-center">
                          <div className="text-xs font-semibold text-red-800">R2</div>
                          <div className="text-xs text-red-600">Busy</div>
                        </div>
                        <div className="bg-green-100 border border-green-300 rounded p-1 text-center">
                          <div className="text-xs font-semibold text-green-800">R3</div>
                          <div className="text-xs text-green-600">Free</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Today's Activity */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs font-semibold text-gray-700 mb-2">Today's Activity</div>
                      <div className="space-y-1">
                        <div className="text-xs text-gray-600">• Deepika - Swedish Massage (R2)</div>
                        <div className="text-xs text-gray-600">• Rajesh - Facial (R1) - ₹2,500</div>
                        <div className="text-xs text-gray-600">• Priya - Package Used (R3)</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
